import { SectionPaper } from '../../components/SectionPaper';
import { TopBar } from '../../components/TopBar';
import { useTranslationPrefix } from '../../../hooks/useTranslationPrefix';
import { useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, IconButton, InputAdornment } from '@mui/material';
import { Formik, Form, FormikHelpers } from 'formik';
import FormikDatePicker from '../../../base/components/form/FormikDatePicker';
import { Box, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as Yup from 'yup';
import { CompanySelect } from '../../../base/components/CompanySelect';

interface FormValues {
  from: Date | null;
  companies: string[];
}

const validationSchema = Yup.object().shape({
  companies: Yup.array()
    .min(1, 'generateQrCounter.companies.required')
    .required('generateQrCounter.companies.required'),
});

const GenerateQrCounterPage: React.VFC = () => {
  const { t } = useTranslationPrefix('companyList');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);

  const handleSubmit = (values: FormValues, { resetForm }: FormikHelpers<any>) => {
    const fromDate = values.from ? new Date(values.from.setHours(0, 0, 0, 0)) : null;

    setSelectedCompanies(values.companies);
    setSelectedFromDate(fromDate);
    setDialogOpen(true);


    resetForm({
      values: {
        from: null,
        companies: values.companies,
      }
    });
  };

  let url = `${window.location.origin}/qr-counter?companyIds=${selectedCompanies.join(',')}`;
  if (selectedFromDate) {
    url += `&from=${selectedFromDate?.toISOString()}`;
  }

  const handleCopyData = () => {
    navigator.clipboard.writeText(url);
  };

  return (
    <>
      <TopBar
        leftSideText={t('generateQrCounter.topBar.title', 'Generate QR Counter')}
      />
      <SectionPaper>
        <Formik
          initialValues={{
            from: null,
            companies: [],
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form>
              <Box display="flex" flexDirection="column" gap={3}>
                <CompanySelect
                  placeholder={t('generateQrCounter.companies.placeholder', 'Choose companies')}
                  multiple
                  id="companies"
                  error={touched.companies && t(errors.companies as string, 'Please select at least one company')}
                  values={values.companies}
                  onValueChange={(newValues) => {
                    setFieldValue('companies', newValues, true);
                  }}
                />
                <FormikDatePicker
                  name="from"
                  label={t('generateQrCounter.from.label', 'From Date')}
                  fullWidth
                />
                <Button
                  id="submit-qr"
                  type="submit"
                  color="primary"
                >
                  {t('generateQrCounter.submit', 'Generate')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </SectionPaper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t('generateQrCounter.dialog.title', 'Generated QR counter link')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={url}
            multiline
            sx={{
              minWidth: '500px'
            }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    id="copy-data-button"
                    onClick={handleCopyData}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GenerateQrCounterPage;
